"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireDefault(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _classnames = _interopRequireDefault(require("classnames"));

var _copyButton = _interopRequireDefault(require("../copy-button"));

var _popover = _interopRequireDefault(require("../popover"));

var _osKey = _interopRequireDefault(require("os-key"));

var _select = _interopRequireDefault(require("select"));

var _getWindow = _interopRequireDefault(require("../utils/get-window"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const DISABLE_CLICK_TO_SELECT_THRESHOLD = 640;
const FEEDBACK_TIME = 2000;
/**
 * Make some text easily copiable.
 *
 * Comes with a [CopyButton](#copybutton) that you can click to automatically
 * copy the text.
 *
 * Also, on wider viewports a click on the text will automatically select it
 * all, so you can easily copy with your favorite keyboard shortcut.
 */

class Copiable extends _react.default.Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "showCopyTooltip", () => {
      this.setState({
        copyTooltipActive: true
      }, () => {
        this.revertTimer = setTimeout(() => {
          this.setState({
            copyTooltipActive: false
          });
        }, FEEDBACK_TIME);
      });
    });

    _defineProperty(this, "setTextEl", el => {
      this.textEl = el;
    });

    _defineProperty(this, "getTextEl", () => {
      return this.textEl;
    });

    _defineProperty(this, "handleTextFocus", () => {
      if ((0, _getWindow.default)().innerWidth < DISABLE_CLICK_TO_SELECT_THRESHOLD) return;
      (0, _select.default)(this.getTextEl());
      this.showCopyTooltip();
    });

    _defineProperty(this, "handleTextBlur", event => {
      if (!this.textEl.contains(event.relatedTarget)) {
        this.setState({
          copyTooltipActive: false
        });
      }
    });

    this.copyHintText = '';
    this.state = {
      copyTooltipActive: false,
      showCopyButton: false
    };
  }

  componentDidMount() {
    this.setState({
      showCopyButton: typeof window !== 'undefined' && _copyButton.default.isCopySupported()
    });
  }

  renderCopyHintText() {
    if (!this.state.showCopyButton) {
      return null;
    }

    return /*#__PURE__*/_react.default.createElement("span", null, /*#__PURE__*/_react.default.createElement("span", {
      className: "txt-kbd"
    }, getCopyKeys((0, _getWindow.default)().navigator.userAgent)), ' ', "to copy");
  }

  componentWillUnmount() {
    clearTimeout(this.revertTimer);
  }

  getWindowWidth() {
    return (0, _getWindow.default)().innerWidth;
  }

  renderCopyButton() {
    const {
      props,
      state
    } = this;

    if (!state.showCopyButton) {
      return null;
    }

    return /*#__PURE__*/_react.default.createElement("div", {
      className: "absolute top right px6 py6"
    }, /*#__PURE__*/_react.default.createElement(_copyButton.default, {
      text: props.value,
      block: true,
      focusTrapPaused: props.focusTrapPaused
    }));
  }

  render() {
    const {
      props
    } = this;
    const copyButton = this.renderCopyButton();

    const copyHintPopover = this.state.copyTooltipActive && /*#__PURE__*/_react.default.createElement(_popover.default, {
      getAnchorElement: this.getTextEl,
      placement: "top",
      alignment: "center",
      hideWhenAnchorIsOffscreen: true,
      accessibleTitle: "Copy the selected text",
      padding: "small"
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: "txt-s"
    }, this.renderCopyHintText()));

    const textClasses = (0, _classnames.default)('my3 txt-mono txt-s mr24', {
      'txt-truncate': props.truncated
    });
    const textStyle = {};

    if (!props.truncated) {
      textStyle.wordWrap = 'break-word';
      textStyle.overflowWrap = 'break-word';
    }

    return /*#__PURE__*/_react.default.createElement("div", {
      className: "relative clearfix bg-darken5 round"
    }, copyButton, /*#__PURE__*/_react.default.createElement("div", {
      tabIndex: "-1",
      ref: this.setTextEl,
      onFocus: this.handleTextFocus,
      onBlur: this.handleTextBlur,
      className: "py6 px12",
      "data-test": "copiable-text-el"
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: textClasses,
      style: textStyle
    }, props.value)), copyHintPopover);
  }

}

exports.default = Copiable;
Copiable.propTypes = {
  /**
   * The text that will be displayed and copied.
   */
  value: _propTypes.default.string.isRequired,

  /**
   * If `true`, this will allow interaction with elements outside of the
   * modal container. You normally don't want to set this, but it can be
   * useful for nesting different components that are displaced to other
   * parts of the DOM.
   */
  focusTrapPaused: _propTypes.default.bool,

  /**
   * If `false` (default), the text will be overflow to multiple lines,
   * and words longer than a single line (e.g. long access tokens or URLs)
   * will be broken to enforce wrapping.
   *
   * If `true`, the Copiable's text will be truncated to a single line
   * of text. **Only set this to `true` if you know that your target
   * browsers support the copy button!** Some browsers will not effectively
   * copy text that is truncated by CSS, so the risk is that some of your
   * users might have *no way* to view and copy all the text if the copy
   * button does not work for them.
   *
   * Horizontal scrolling is not an option because of things end up getting
   * pretty gross across browsers.
   */
  truncated: _propTypes.default.bool
};
Copiable.defaultProps = {
  truncated: false
};

function getCopyKeys(ua) {
  const keys = (0, _osKey.default)(ua);
  if (!keys) return;

  if (keys.primaryMeta) {
    // ⌘+C
    return `${keys.meta.symbol}+C`;
  } else {
    // Ctrl+C
    return `${keys.ctrl.abbreviation}+C`;
  }
}